import type { ChargeBreakdown, DetailedPrice, DetailedPriceCharge, EffectivePriceSegment, MultiCurrencyPrice, Price } from '../api/models/bff/Price';
import type { PlanDiscount } from '../api/models/bff/Plan';
import type { EffectivePriceInfo } from './priceCalculation.model';

/** A priced entity carrying the campaign-aware pricing fields (plan or add-on). */
export interface Discountable {
  price: DetailedPrice;
  effectivePrice?: MultiCurrencyPrice;
  discounts?: PlanDiscount[];
}

export function pickByCurrency(currency: string, prices?: Price[]): Price | undefined {
  if (!prices?.length) return undefined;

  return prices.find(price => price.currency === currency) ?? prices[0];
}

/** Keeps only the charges quoted in `currency`; a charge missing that currency is dropped. */
export function chargesByCurrency(breakdowns: ChargeBreakdown[] | undefined, currency: string): DetailedPriceCharge[] {
  return (breakdowns ?? []).flatMap(({ amounts, ...breakdown }) => {
    const match = amounts.find(entry => entry.currency === currency);

    return match ? [{ ...breakdown, amount: match.amount }] : [];
  });
}

/**
 * Resolves a multi-currency price to the single-currency shape the UI renders, falling back to the
 * first quoted currency when `preferredCurrency` is not offered. The currency settled on for `gross`
 * then drives `net` and the charges, so one entity never mixes currencies.
 *
 * Returns undefined when the price quotes no currency, or quotes no `net` for the settled currency —
 * callers decide between rendering nothing and failing, rather than showing a fabricated zero.
 */
export function toDetailedPrice(price: MultiCurrencyPrice, preferredCurrency: string): DetailedPrice | undefined {
  const gross = pickByCurrency(preferredCurrency, price.gross);
  const net = gross && price.net.find(entry => entry.currency === gross.currency);
  if (!gross || !net) return undefined;

  return {
    currency: gross.currency,
    gross: gross.amount,
    net: net.amount,
    additionalCharges: chargesByCurrency(price.additionalCharges, gross.currency),
  };
}

export function includedDiscount(entity: Discountable): PlanDiscount | undefined {
  return entity.discounts?.find(discount => discount.includedInEffectivePrice);
}

export function effectiveNet(entity: Discountable, currency: string): number {
  const effective = pickByCurrency(currency, entity.effectivePrice?.net)?.amount;

  return parseFloat(effective ?? entity.price.net);
}

export function discountGross(entity: Discountable, currency: string): number {
  const amount = pickByCurrency(currency, includedDiscount(entity)?.gross)?.amount;

  return amount ? parseFloat(amount) : 0;
}

export function effectiveCharges(entity: Discountable, currency: string): DetailedPriceCharge[] {
  const breakdowns = entity.effectivePrice?.additionalCharges;

  return breakdowns ? chargesByCurrency(breakdowns, currency) : entity.price.additionalCharges;
}

function findFullPriceIndex(schedule: EffectivePriceSegment[]): number {
  return schedule.map(segment => segment.promotional).lastIndexOf(false);
}

function fractionDigits(amount: string): number {
  return amount.split('.')[1]?.length ?? 0;
}

function grossSavings(fullPrice: Price | undefined, current: EffectivePriceSegment | null): Price | undefined {
  if (!fullPrice || !current) return undefined;

  const currentGross = current.price.gross.amount;
  const decimals = Math.max(fractionDigits(fullPrice.amount), fractionDigits(currentGross));
  const amount = parseFloat(fullPrice.amount) - parseFloat(currentGross);
  return { amount: amount.toFixed(decimals), currency: fullPrice.currency };
}

export function deriveEffectivePrice(schedule: EffectivePriceSegment[] | null | undefined): EffectivePriceInfo {
  const currentSegment = schedule?.[0] ?? null;
  const isPromotional = currentSegment?.promotional ?? false;
  const fullPriceIndex = schedule ? findFullPriceIndex(schedule) : -1;

  const isEvergreen = isPromotional && fullPriceIndex === -1;
  const fullPrice = fullPriceIndex >= 0 ? schedule![fullPriceIndex].price.gross : undefined;
  const revertsFrom = fullPriceIndex > 0 ? schedule![fullPriceIndex - 1].validUntil : null;
  const revertsAt = revertsFrom ? new Date(revertsFrom) : null;

  return {
    currentSegment,
    isPromotional,
    isEvergreen,
    revertsAt,
    fullPrice,
    savings: grossSavings(fullPrice, currentSegment),
  };
}